import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import Table from "../../../../components/TableV2";
import TitleButtons from "../../../../components/TitleButtons";
import LeftTable from "../../../../components/LeftTable";
import { useStandbySuprimentos } from "./standbyData-servico";
import Modal from "../../../../components/Modal";
import { generatePdfReport } from "../../../../utils/pdf/Report-Standby-Technical";
import { generatePdfReportAllOS } from "../../../../utils/pdf/Report-Standby-All-OS";
import ModalItemsGrid from "../../../../components/ModalItemsGrid";

type Row = (string | number | React.ReactNode)[];

const IDX = {
  OS: 0,
  TIPO: 1,
  CODIGO: 2,
  NOME: 3,
  QTD: 4,
  CUSTO: 5,
  TECNICO: 6,
  DATA: 7,
  HORA: 8,
  // 9 = "Dias de Atraso"
};

type RawProduto = { codigo: string; nome: string; posicao?: string | null };
type RawItem = {
  id: number;
  movimentacao_id: number;
  produto_id: number;
  quantidade: number;
  custo_unitario: string | null;
  produto: RawProduto;
  preco_venda?: string | number | null;
  moeda?: string | null;
  pendente_retorno?: boolean | null;
};

type RawEmpresa = {
  id: string;
  nome_fantasia?: string | null;
  responsaveis_ids?: number[] | null;
  responsaveis_nomes?: string[] | null;
};

type RawMov = {
  movimentacao: {
    id: number;
    responsavel_id: number | null;
    responsavel_nome: string | null;
    departamento_origem: string | null;
    tipo: string | null;
    os_numero?: string | null;
    coletor_nome?: string | null;
    criado_em: string;
    embarcacao?: string | null;
    comentario?: string | null;
    pendente_retorno?: boolean | null;
    empresa?: RawEmpresa | null;
  };
  itens: RawItem[];
};

const API_BASE = "https://api.eva-googlemarine.com";
const URL_LIST = `${API_BASE}/departamentos/suprimentos/standby`;
const URL_RETORNAR = `${API_BASE}/movimentar-produtos-retornar`;
const URL_BAIXAR = `${API_BASE}/movimentar-produtos-baixar`;

/* ================= Helpers ================= */
function parseBRL(s: any) {
  return typeof s === "string"
    ? Number(
        String(s)
          .replace(/[^\d,.-]/g, "")
          .replace(/\./g, "")
          .replace(",", ".")
      )
    : Number(s ?? 0);
}
function toStartOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function formatFirstTwoNames(nome: string | null | undefined): string {
  if (!nome) return "-";
  const parts = String(nome).trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 2) return parts.join(" ");
  return `${parts[0]} ${parts[1]}`;
}
function parseDateSmart(s: string): Date | null {
  if (!s) return null;
  const t = s.trim();
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(t);
  if (m) {
    const dd = Number(m[1]);
    const MM = Number(m[2]) - 1;
    const yyyy = Number(m[3]);
    const d = new Date(yyyy, MM, dd);
    return isNaN(d.getTime()) ? null : d;
  }
  const d2 = new Date(t);
  return isNaN(d2.getTime()) ? null : d2;
}
function diffDaysFromToday(dateStr: string): number | null {
  const d = parseDateSmart(dateStr);
  if (!d) return null;
  const today = toStartOfDay(new Date());
  const start = toStartOfDay(d);
  const ms = today.getTime() - start.getTime();
  return Math.max(0, Math.floor(ms / 86400000));
}

const SuprimentosStandby: React.FC = () => {
  const headers = useMemo(
    () => [
      "OS",
      "C. Responsável",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "Custo Unitário",
      "Técnico",
      "Data",
      "Hora",
      "Dias de Atraso",
    ],
    []
  );

  const columnWidths = useMemo(
    () => [
      "60px",
      "80px",
      "70px",
      "300px",
      "10px",
      "100px",
      "100px",
      "80px",
      "60px",
      "70px",
    ],
    []
  );

  const { rows, loading, refresh } = useStandbySuprimentos();

  /* ===== LISTA: acrescenta “Dias de Atraso” e propaga pendência ===== */
  const tableDisplayRows = useMemo<Row[]>(() => {
    return (rows as any[]).map((r) => {
      const clone = (Array.isArray(r) ? r.slice() : []) as Row;

      const tecnicoRaw = String(clone[IDX.TECNICO] ?? "");
      clone[IDX.TECNICO] = formatFirstTwoNames(tecnicoRaw);

      const dataStr = String(r[IDX.DATA] ?? "");
      const days = diffDaysFromToday(dataStr);
      clone.push(days == null ? "-" : String(days));

      const pend =
        Boolean((r as any)?.__pendente_retorno) ||
        Boolean((r as any)?.pendente_retorno);
      (clone as any).__pendente_retorno = pend;

      return clone;
    });
  }, [rows]);

  /* ===== Abertura do modal por duplo clique ===== */
  const [openOS, setOpenOS] = useState<string | null>(null);

  const handleDoubleClick = useCallback(
    (_viewIndex: number, rowData: Row, _originalIndex?: number) => {
      const os = String(rowData[IDX.OS] ?? "").trim();
      if (!os) return;
      setOpenOS(os);
    },
    []
  );

  /* ===== RAW para modal/detalhes ===== */
  const [rawMovs, setRawMovs] = useState<RawMov[] | null>(null);
  const [busy, setBusy] = useState(false);

  // ✅ multi-select: Set de indexes
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(
    new Set()
  );
  const selectedCount = selectedIndexes.size;

  useEffect(() => {
    // sempre que trocar OS, limpa seleção
    setSelectedIndexes(new Set());
  }, [openOS]);

  useEffect(() => {
    let cancelled = false;
    async function loadRaw() {
      if (!openOS) return;
      setBusy(false);
      try {
        const res = await fetch(URL_LIST, {
          headers: { Accept: "application/json" },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const data = Array.isArray(json?.data) ? (json.data as RawMov[]) : [];
        const onlyThisOS = data.filter(
          (m) => (m.movimentacao.os_numero || "-") === openOS
        );
        if (!cancelled) setRawMovs(onlyThisOS);
      } catch (e) {
        console.error("Falha ao carregar detalhes RAW da O.S.:", e);
        if (!cancelled) setRawMovs(null);
      }
    }
    loadRaw();
    return () => {
      cancelled = true;
    };
  }, [openOS]);

  const embarcacaoNome = useMemo(() => {
    if (!rawMovs?.length) return "-";
    const mv = rawMovs.find((m) => (m.movimentacao.embarcacao || "").trim());
    return (mv?.movimentacao.embarcacao || "-").trim();
  }, [rawMovs]);

  const empresaNome = useMemo(() => {
    if (!rawMovs?.length) return "-";
    const mv = rawMovs.find(
      (m) =>
        m.movimentacao.empresa &&
        (m.movimentacao.empresa.nome_fantasia || "").trim()
    );
    return (mv?.movimentacao.empresa?.nome_fantasia || "-").trim();
  }, [rawMovs]);

  const osDetails = useMemo(() => {
    if (!openOS) return null;
    const itens = (rows as Row[]).filter((r) => String(r[IDX.OS]) === openOS);
    if (!itens.length)
      return {
        info: [] as { label: string; value: string }[],
        headers: [] as string[],
        tableRows: [] as Row[],
        colWidths: [] as string[],
        totalMateriais: 0,
        totalUnidades: 0,
        totalCusto: 0,
      };

    const first = itens[0];
    const data = String(first[IDX.DATA] ?? "-");
    const hora = String(first[IDX.HORA] ?? "-");
    const tecnico = String(first[IDX.TECNICO] ?? "-");

    const totalUnidades = itens.reduce(
      (acc, r) => acc + Number(r[IDX.QTD] ?? 0),
      0
    );
    const totalMateriais = itens.length;
    const totalCusto = itens.reduce(
      (acc, r) => acc + parseBRL(r[IDX.CUSTO]) * Number(r[IDX.QTD] ?? 0),
      0
    );

    const info = [
      { label: "Número da O.S", value: openOS },
      { label: "Responsável/Técnico", value: tecnico || "-" },
      { label: "Empresa", value: empresaNome || "-" },
      { label: "Embarcação", value: embarcacaoNome || "-" },
      { label: "Data", value: data },
      { label: "Hora", value: hora },
      {
        label: "Total de Materiais Coletados",
        value: `${totalMateriais} itens, ${totalUnidades} unidades`,
      },
      {
        label: "Custo Total (aprox.)",
        value:
          isFinite(totalCusto) && totalCusto > 0
            ? totalCusto.toLocaleString("pt-BR", {
                style: "currency",
                currency: "BRL",
              })
            : "-",
      },
    ];

    const detailsHeaders = [
      "C.Responsável",
      "Código",
      "Nome do Produto",
      "Quantidade",
      "Responsável",
      "Data",
      "Hora",
    ];
    const detailsColWidths = [
      "130px",
      "100px",
      "480px",
      "110px",
      "250px",
      "100px",
      "70px",
    ];
    const tableRows = itens.map((r) => [
      String(r[IDX.TIPO] ?? "-"),
      String(r[IDX.CODIGO] ?? "-"),
      String(r[IDX.NOME] ?? "-"),
      String(r[IDX.QTD] ?? "-"),
      String(r[IDX.TECNICO] ?? "-"),
      String(r[IDX.DATA] ?? "-"),
      String(r[IDX.HORA] ?? "-"),
    ]);

    return {
      info,
      headers: detailsHeaders,
      colWidths: detailsColWidths,
      tableRows,
      totalMateriais,
      totalUnidades,
      totalCusto,
    };
  }, [openOS, rows, embarcacaoNome, empresaNome]);

  const responsavelId = useMemo(() => {
    if (!rawMovs?.length) return null;
    const withResp = rawMovs.find((m) => m.movimentacao.responsavel_id != null);
    return withResp?.movimentacao.responsavel_id ?? null;
  }, [rawMovs]);

  async function postJSON(url: string, payload: any) {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status} – ${t || "erro ao enviar"}`);
    }
    return res.json().catch(() => ({}));
  }

  async function askQuantidade(max: number): Promise<number | null> {
    const val = window.prompt(
      `Quantidade (máxima: ${max})`,
      String(Math.min(1, max))
    );
    if (val == null) return null;
    const n = Number(String(val).replace(",", "."));
    if (!Number.isFinite(n) || n <= 0) {
      window.alert("Informe um número válido maior que zero.");
      return null;
    }
    if (!Number.isInteger(n)) {
      window.alert("A quantidade deve ser inteira.");
      return null;
    }
    if (n > max) {
      window.alert(`A quantidade não pode exceder ${max}.`);
      return null;
    }
    return n;
  }

  function mapUiToRawItem(data: RawMov[], uiRow: Row): RawItem | null {
    const codigo = String(uiRow[1] ?? "-");
    const nome = String(uiRow[2] ?? "-");
    const qtd = Number(uiRow[3] ?? 0);
    for (const m of data)
      for (const it of m.itens || [])
        if (
          it.produto?.codigo === codigo &&
          it.produto?.nome === nome &&
          Number(it.quantidade) === qtd
        )
          return it;
    for (const m of data)
      for (const it of m.itens || [])
        if (it.produto?.codigo === codigo && Number(it.quantidade) === qtd)
          return it;
    for (const m of data)
      for (const it of m.itens || [])
        if (it.produto?.codigo === codigo) return it;
    return null;
  }

  // ===== Multi-select helpers =====
  const lastClickedRef = useRef<number | null>(null);

  const clearSelection = useCallback(() => {
    setSelectedIndexes(new Set());
    lastClickedRef.current = null;
  }, []);

  const handleGridRowClick = useCallback(
    (idx: number, ev?: React.MouseEvent) => {
      const isRange = !!ev?.shiftKey;
      const isMulti = !!(ev?.ctrlKey || ev?.metaKey);

      if (isRange && lastClickedRef.current != null && osDetails?.tableRows) {
        const a = lastClickedRef.current;
        const b = idx;
        const start = Math.min(a, b);
        const end = Math.max(a, b);
        setSelectedIndexes((prev) => {
          const next = new Set(prev);
          for (let i = start; i <= end; i++) next.add(i);
          return next;
        });
      } else if (isMulti) {
        setSelectedIndexes((prev) => {
          const next = new Set(prev);
          if (next.has(idx)) next.delete(idx);
          else next.add(idx);
          return next;
        });
      } else {
        setSelectedIndexes(new Set([idx]));
      }

      lastClickedRef.current = idx;
    },
    [osDetails]
  );

  // ===== Relatórios =====
  const handleGerarRelatorioDaOs = useCallback(async () => {
    if (!openOS || !osDetails) {
      return window.alert("Abra uma O.S. para gerar o relatório completo.");
    }
    if (!rawMovs || !rawMovs.length) {
      return window.alert("Dados RAW da O.S. indisponíveis.");
    }

    const allItems = rawMovs.flatMap((m) =>
      (m.itens || []).map((it) => ({
        codigo: it.produto?.codigo || "-",
        nome: it.produto?.nome || "-",
        quantidade: Number(it.quantidade ?? 0),
        custoUnitario: (() => {
          const rawStr = it.custo_unitario;
          if (rawStr == null) return null;
          const n = Number(String(rawStr).replace(",", "."));
          return Number.isFinite(n) ? n : null;
        })(),
        tecnico: m.movimentacao.coletor_nome || "-",
      }))
    );

    const tecnicosSet = new Set<string>();
    rawMovs.forEach((m) => {
      const t = m.movimentacao.coletor_nome || "";
      if (t && t.trim()) tecnicosSet.add(t.trim());
    });
    const tecnicos = Array.from(tecnicosSet);

    try {
      await generatePdfReportAllOS({
        osNumero: openOS,
        tecnicos,
        materiais: allItems,
      });
    } catch (e: any) {
      window.alert(
        `Falha ao gerar o relatório completo da O.S.: ${e?.message || e}`
      );
    }
  }, [openOS, osDetails, rawMovs]);

  // ✅ NOVO: relatório dos selecionados (1 PDF por técnico)
  const handleGerarRelatorioSelecionadosPorTecnico = useCallback(async () => {
    if (!openOS || !osDetails) {
      return window.alert("Abra uma O.S. e selecione itens.");
    }
    if (selectedIndexes.size === 0) {
      return window.alert("Selecione pelo menos 1 item.");
    }

    const byTec: Record<
      string,
      { codigo: string; nome: string; quantidade: number }[]
    > = {};

    for (const idx of Array.from(selectedIndexes).sort((a, b) => a - b)) {
      const ui = osDetails.tableRows[idx];
      if (!ui) continue;

      const tecnicoNome = String(ui[4] ?? "-").trim() || "-";
      const item = {
        codigo: String(ui[1] ?? "-"),
        nome: String(ui[2] ?? "-"),
        quantidade: Number(ui[3] ?? 0),
      };

      (byTec[tecnicoNome] ||= []).push(item);
    }

    try {
      // gera um PDF por técnico (melhor pro seu caso)
      for (const [tecnicoNome, materiais] of Object.entries(byTec)) {
        await generatePdfReport({ osNumero: openOS, tecnicoNome, materiais });
      }
    } catch (e: any) {
      window.alert(`Falha ao gerar o relatório: ${e?.message || e}`);
    }
  }, [openOS, osDetails, selectedIndexes]);

  // ===== Retornos =====
  const handleRetornarOSInteira = useCallback(async () => {
    if (!openOS || !rawMovs) return;
    if (responsavelId == null)
      return window.alert("Sem responsável definido para esta O.S.");

    const itens = rawMovs
      .flatMap((m) => m.itens || [])
      .filter((it) => Number(it.quantidade) > 0)
      .map((it) => ({
        movimentacao_id: it.movimentacao_id,
        produto_id: it.produto_id,
        quantidade: Number(it.quantidade),
      }));

    if (!itens.length)
      return window.alert("Não há itens com quantidade para retornar.");

    const payload = { responsavel_id: responsavelId, itens };

    try {
      setBusy(true);
      await postJSON(URL_RETORNAR, payload);
      window.alert("Todos os itens foram retornados ao estoque.");
      await refresh?.();
      setOpenOS(null);
    } catch (e: any) {
      window.alert(`Falha ao retornar O.S.: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [openOS, rawMovs, responsavelId, refresh]);

  const handleRetornarItem = useCallback(async () => {
    if (!openOS || !osDetails) return;
    if (selectedIndexes.size !== 1)
      return window.alert(
        "Para retorno por item, selecione exatamente 1 item."
      );
    if (!rawMovs || responsavelId == null)
      return window.alert("Dados da O.S. indisponíveis (sem responsável).");

    const selectedIndex = Array.from(selectedIndexes)[0];

    const pool = rawMovs.flatMap((m) => m.itens || []);
    const ui = osDetails.tableRows[selectedIndex];
    if (!ui) return;

    const codigo = String(ui[1] ?? "-");
    const nome = String(ui[2] ?? "-");
    const qtd = Number(ui[3] ?? 0);

    const rawItem =
      pool.find(
        (it) =>
          it.produto?.codigo === codigo &&
          it.produto?.nome === nome &&
          Number(it.quantidade) === qtd
      ) ||
      pool.find(
        (it) => it.produto?.codigo === codigo && Number(it.quantidade) === qtd
      ) ||
      pool.find((it) => it.produto?.codigo === codigo) ||
      null;

    if (!rawItem)
      return window.alert("Não foi possível localizar o item selecionado.");

    const max = Number(rawItem.quantidade ?? 0);
    if (!(max > 0))
      return window.alert("Item sem quantidade disponível para retorno.");

    const qtdSel = await askQuantidade(max);
    if (qtdSel == null) return;

    const payload = {
      responsavel_id: responsavelId,
      itens: [
        {
          movimentacao_id: rawItem.movimentacao_id,
          produto_id: rawItem.produto_id,
          quantidade: qtdSel,
        },
      ],
    };

    try {
      setBusy(true);
      await postJSON(URL_RETORNAR, payload);
      window.alert("Retorno realizado com sucesso.");
      await refresh?.();
      setOpenOS(null);
    } catch (e: any) {
      window.alert(`Falha ao retornar item: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  }, [openOS, osDetails, rawMovs, responsavelId, refresh, selectedIndexes]);

  const handleModalClose = useCallback(() => {
    if (busy) return;
    setOpenOS(null);
  }, [busy]);

  const canAct = !!openOS && !busy;
  const canGerarSelecionados = canAct && selectedCount > 0;
  const canRetornarItem =
    canAct && selectedCount === 1 && !!rawMovs && responsavelId != null;

  return (
    <div>
      <TitleButtons fullBleedLine title="Suprimentos - Standby Serviço" />

      <Table
        headers={headers}
        rows={tableDisplayRows}
        columnWidths={columnWidths}
        isTableStandby
        onDoubleClick={handleDoubleClick}
        enableCellColors={false}
        atrasoColumnIndex={9}
        delayScope="cell"
        enableZebra={false}
        isRowPending={(_row) => Boolean((_row as any)?.__pendente_retorno)}
        responsiveWidths={{
          1300: {
            1: "60px",
            2: "0px",
            3: "300px",
            4: "0px",
            5: "100px",
            6: "100px",
            7: "60px",
            8: "60px",
            9: "60px",
          },
        }}
      />

      <Modal open={!!openOS} onClose={handleModalClose} maxWidth={1280}>
        {openOS && osDetails && (
          <div
            style={{
              padding: 0,
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 8,
              width: "100%",
            }}
          >
            <div style={{ width: "100%" }}>
              <LeftTable data={osDetails.info} />
            </div>

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 4,
              }}
            >
              <button
                disabled={busy || !rawMovs || responsavelId == null}
                onClick={handleRetornarOSInteira}
                style={{
                  marginBottom: "8px",
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #0a7",
                  background: "#f0fffa",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Retorna todos os itens desta O.S. ao estoque"
              >
                Retornar O.S. ao estoque
              </button>

              <button
                disabled={busy || !rawMovs || responsavelId == null}
                onClick={async () => {
                  try {
                    await handleGerarRelatorioDaOs();
                  } catch {}
                }}
                style={{
                  marginBottom: "8px",
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #C27B30FF",
                  background: "#FCF1E5FF",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Gerar relatório completo da O.S."
              >
                Gerar Relatório da O.S
              </button>

              <button
                disabled={busy || selectedCount === 0}
                onClick={clearSelection}
                style={{
                  marginBottom: "8px",
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #999",
                  background: "#fff",
                  cursor: busy ? "not-allowed" : "pointer",
                }}
                title="Limpar seleção de itens"
              >
                Limpar Seleção ({selectedCount})
              </button>

              <button
                disabled={!canGerarSelecionados}
                onClick={handleGerarRelatorioSelecionadosPorTecnico}
                style={{
                  marginBottom: "8px",
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #C27B30FF",
                  background: "#FCF1E5FF",
                  cursor: !canGerarSelecionados ? "not-allowed" : "pointer",
                }}
                title="Gera relatório dos itens selecionados (um PDF por técnico)"
              >
                Gerar Relatório (Selecionados: {selectedCount})
              </button>
            </div>

            <ModalItemsGrid
              headers={osDetails.headers}
              colWidths={osDetails.colWidths}
              rows={osDetails.tableRows}
              // ✅ novo: multi-select
              selectedIndexes={Array.from(selectedIndexes)}
              onRowClick={handleGridRowClick}
              centerCols={[0, 1, 2, 3, 4, 5, 7, 8]}
              perColumnRender={{
                "Preço de Venda": (uiRow) => {
                  let precoDisplay: string | React.ReactNode = "-";
                  if (rawMovs && rawMovs.length) {
                    const found = mapUiToRawItem(rawMovs, uiRow as Row);
                    const pv = found?.preco_venda;
                    const moeda = String(found?.moeda || "BRL").toUpperCase();
                    if (
                      pv === "pendente informar" ||
                      String(pv).toLowerCase() === "pendente informar"
                    ) {
                      precoDisplay = "pendente informar";
                    } else if (pv != null && pv !== "") {
                      const n = Number(String(pv).replace(",", "."));
                      if (Number.isFinite(n)) {
                        try {
                          precoDisplay = new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: moeda,
                          }).format(n);
                        } catch {
                          precoDisplay = `R$ ${n.toLocaleString("pt-BR", {
                            minimumFractionDigits: 2,
                          })}`;
                        }
                      }
                    }
                  }
                  return precoDisplay;
                },
                Moeda: (uiRow) => {
                  if (rawMovs && rawMovs.length) {
                    const found = mapUiToRawItem(rawMovs, uiRow as Row);
                    return String(found?.moeda || "BRL").toUpperCase();
                  }
                  return "-";
                },
              }}
            />

            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginTop: 8,
              }}
            >
              <button
                disabled={!canRetornarItem}
                onClick={handleRetornarItem}
                style={{
                  padding: "12px 12px",
                  borderRadius: 0,
                  border: "1px solid #08a",
                  background: "#f0f8ff",
                  cursor: !canRetornarItem ? "not-allowed" : "pointer",
                }}
                title="Retorna somente o item selecionado"
              >
                Retornar produto ao estoque (1 item)
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SuprimentosStandby;
